import { getCoarsestCdf } from 'features/assetVizAdapter/forecastAssetVizWindow/resolutionOptions'
import { useFfsAssetModelSnapshot } from 'features/libra/stepComponents/ffs/useFfsAssetModelSnapshot'
import { resolveCdfComponentInspections } from 'features/libra/ticket/cdfComponentInspection'
import { useGeckoLocale } from 'features/locale'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoInspection,
  GeckoView,
  RolodexEntryType,
} from 'types'

export interface UseFfsComponentCdfsArgs {
  componentId: string | undefined
  inspectionId: string | undefined
}

export interface UseFfsComponentCdfsResult {
  /** CDFs for this (component, inspection), respecting the CDS bridge. */
  cdfs: ReadonlyArray<GeckoComponentDataFrame>
  /** The thickness CDF, if one exists in `cdfs`. */
  thicknessCdf: GeckoComponentDataFrame | undefined
  /** Co-fetched from the same batched query -- exposed for callers that want them. */
  assetModel: GeckoAssetModel | undefined
  inspection: GeckoInspection | undefined
  component: GeckoComponent | undefined
  /**
   * Component datasets matching this (component, inspection) -- already
   * filtered. The binning step consumes the first one as the LTA CDS.
   */
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
  isLoading: boolean
  /**
   * True only while the queries that produce `cdfs` are in flight -- excludes
   * the asset-model snapshot, which `isLoading` also covers. Callers that just
   * need a CDF (e.g. the observed-minimum check) should wait on this instead,
   * so they don't block on an entry they never read.
   */
  cdfsLoading: boolean
  /** Refresh both the component query and the unit-scoped batched query. */
  refresh: () => Promise<void>
}

/**
 * Shared CDF discovery for libra FFS steps. Mirrors `FfsAssetVizPanel`'s
 * exact query shape so SWR deduplicates the fetch across panels.
 *
 * Three things matter for the lookup to return the expected CDFs:
 *
 *  1. **Derive `unitId` from the component entry** (`component.tags[UNIT]`),
 *     not the libra ticket's tags -- tickets can be created without a unit
 *     tag or with a stale one.
 *  2. **Query an array of four types in one call** -- the Rolodex backend
 *     handles this shape reliably; a single-type tag-based query is a
 *     different code path and has been observed to return empty results.
 *  3. **Filter CDFs through the CDS bridge** -- a CDF may declare its
 *     (component, inspection) only indirectly via its `component-dataset`
 *     tag pointing at a `GeckoComponentDataset` that carries those tags.
 */
export function useFfsComponentCdfs({
  componentId,
  inspectionId,
}: UseFfsComponentCdfsArgs): UseFfsComponentCdfsResult {
  const {
    entries: componentEntries,
    isLoading: compLoading,
    forceRefresh: refreshComponentQuery,
  } = useRolodexEntriesWithSyncedRefreshes(
    componentId
      ? {
          ids: [componentId],
          type: RolodexEntryType.COMPONENT,
          links: [RolodexEntryType.UNIT],
        }
      : undefined
  )
  const component = componentEntries[0] as GeckoComponent | undefined
  const derivedUnitId = component?.tags[RolodexEntryType.UNIT] as
    | string
    | undefined

  const {
    inspections,
    componentDataFrames,
    componentDatasets: unfilteredComponentDatasets,
    isLoading: unitLoading,
    forceRefresh: refreshUnitQuery,
  } = useRolodexEntriesWithSyncedRefreshes(
    derivedUnitId
      ? {
          type: [
            RolodexEntryType.INSPECTION,
            RolodexEntryType.COMPONENT_DATA_FRAME,
            RolodexEntryType.COMPONENT_DATASET,
          ],
          tags: { [RolodexEntryType.UNIT]: derivedUnitId },
        }
      : undefined
  )

  const componentDatasets = useMemo<
    ReadonlyArray<GeckoComponentDataset>
  >(() => {
    if (!componentId || !inspectionId) return []
    return (
      unfilteredComponentDatasets as ReadonlyArray<GeckoComponentDataset>
    ).filter(
      (cds) =>
        cds.tags.component === componentId &&
        cds.tags.inspection === inspectionId
    )
  }, [unfilteredComponentDatasets, componentId, inspectionId])

  const cdfs = useMemo<ReadonlyArray<GeckoComponentDataFrame>>(() => {
    if (!componentId || !inspectionId) return []
    // A CDF qualifies for this (component, inspection) if it attests to it
    // either directly (its own tags) or via the CDS bridge (its
    // `component-dataset` points at a CDS carrying those tags). The shared
    // `resolveCdfComponentInspections` resolver owns that rule -- the same one
    // the tab layer uses in `buildInspectionsWithCdfData`.
    const datasetById = new Map<string, GeckoComponentDataset>(
      (unfilteredComponentDatasets as ReadonlyArray<GeckoComponentDataset>).map(
        (cds) => [cds.id, cds]
      )
    )
    return (
      componentDataFrames as ReadonlyArray<GeckoComponentDataFrame>
    ).filter((cdf) =>
      resolveCdfComponentInspections(cdf, datasetById).some(
        (pair) =>
          pair.component === componentId && pair.inspection === inspectionId
      )
    )
  }, [
    componentDataFrames,
    componentId,
    inspectionId,
    unfilteredComponentDatasets,
  ])

  const { getPreferredMeasurementUnit } = useGeckoLocale()

  // Pick the thickness layer, then coarsen to the largest bin in its
  // resolution family -- the SAME pick `FfsAssetVizPanel` makes for the
  // viewer. This is not cosmetic: stats are cached by `cdf.id`, so a raw
  // `.find()` here (Rolodex return order, no bin preference) routinely
  // resolved a DIFFERENT, finer CDF than the one on screen. That meant a
  // second full-grid parquet read + worker pass to recompute a minimum the
  // viz had already computed. Sharing the pick means sharing the cache entry.
  const thicknessCdf = useMemo(() => {
    const firstThickness = cdfs.find(
      (cdf) => cdf.data.params.view === GeckoView.Thickness
    )
    if (!firstThickness) return undefined
    return getCoarsestCdf({
      cdfs,
      selectedCDF: firstThickness,
      getPreferredMeasurementUnit,
    })
  }, [cdfs, getPreferredMeasurementUnit])

  const inspection = useMemo(
    () =>
      inspectionId
        ? (inspections.find((i) => i.id === inspectionId) as
            | GeckoInspection
            | undefined)
        : undefined,
    [inspections, inspectionId]
  )

  // Snapshot-first asset-model resolution shared with `useFfsTicketContext`
  // so every FFS step + panel binds to the same entry (the ticket's
  // snapshot for asset-scoped workflows, the id-sorted live pick
  // otherwise). The hook derives the canonical unitId itself.
  const {
    assetModel,
    isLoading: assetModelLoading,
    forceRefresh: refreshAssetModel,
  } = useFfsAssetModelSnapshot()

  const refresh = useCallback(async () => {
    await Promise.all([
      refreshComponentQuery(),
      refreshUnitQuery(),
      refreshAssetModel(),
    ])
  }, [refreshComponentQuery, refreshUnitQuery, refreshAssetModel])

  return {
    cdfs,
    thicknessCdf,
    assetModel,
    inspection,
    component,
    componentDatasets,
    isLoading: compLoading || unitLoading || assetModelLoading,
    cdfsLoading: compLoading || unitLoading,
    refresh,
  }
}
